/**
 * Thin fetch wrapper used by all frontend API modules.
 * Reads the backend URL from Vite's env variable at build time.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";

/** Error raised when the API responds with a non-2xx status. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function extractErrorMessage(data: unknown, status: number): string {
  if (typeof data === "string") {
    return data;
  }

  if (typeof data === "object" && data !== null) {
    const detail = (data as Record<string, unknown>).detail;

    if (typeof detail === "string") {
      return detail;
    }

    // FastAPI validation errors: [{ loc: [...], msg: "...", type: "..." }]
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            const loc = (item as Record<string, unknown>).loc;
            const msg = (item as Record<string, unknown>).msg;
            const locStr = Array.isArray(loc) ? loc.join(".") : "";
            return `${locStr ? locStr + ": " : ""}${msg ?? JSON.stringify(item)}`;
          }
          return String(item);
        })
        .filter(Boolean);
      if (messages.length) {
        return messages.join("; ");
      }
    }

    // Fallback: pretty-print the whole error object.
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }

  return `Request failed with status ${status}`;
}

/** Parse the response body and throw ApiError on HTTP failures. */
async function handleResponse<T>(response: Response): Promise<T> {
  // No-content responses (e.g., 204 DELETE) should not be parsed as JSON even
  // if the server sets a JSON content-type with an empty body.
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0" ||
    (await response.clone().text()) === ""
  ) {
    return undefined as T;
  }

  let data: unknown;
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message = extractErrorMessage(data, response.status);
    throw new ApiError(response.status, message);
  }

  return data as T;
}

/** Perform an authenticated GET request. */
export async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return handleResponse<T>(response);
}

/** Perform an authenticated POST request with a JSON body. */
export async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(response);
}

/** Perform an authenticated PUT request with a JSON body. */
export async function put<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(response);
}

/** Perform an authenticated PATCH request with a JSON body. */
export async function patch<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(response);
}

/** Perform an authenticated DELETE request. */
export async function del(path: string): Promise<void> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  return handleResponse<void>(response);
}
